import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { CreateDriverRequest, Site, apiClient } from '../../services/api';
import { validate, collectErrors } from '../../utils/validation';

interface AddDriverModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (driver: CreateDriverRequest) => Promise<void>;
    isCreating: boolean;
}

type FormErrors = Partial<Record<keyof CreateDriverRequest, string>>;

const emptyForm: CreateDriverRequest = {
    firstName: '',
    lastName: '',
    licenceNumber: '',
    email: '',
    dateOfBirth: '',
    addressLine1: '',
    addressLine2: '',
    phone: '',
    city: '',
    postcode: '',
    siteId: '',
};

const AddDriverModal: React.FC<AddDriverModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isCreating
}) => {
    const [newDriver, setNewDriver] = useState<CreateDriverRequest>(emptyForm);
    const [errors, setErrors] = useState<FormErrors>({});
    const [sites, setSites] = useState<Site[]>([]);
    const [sitesLoading, setSitesLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadSites();
        }
    }, [isOpen]);

    const loadSites = async () => {
        try {
            setSitesLoading(true);
            const response = await apiClient.getSites({ active: true, pageSize: 1000 });
            setSites(response.data);
        } catch (err) {
            console.error('Error loading sites:', err);
        } finally {
            setSitesLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const result = collectErrors({
            firstName: validate.required(newDriver.firstName, 'First name'),
            lastName: validate.required(newDriver.lastName, 'Last name'),
            licenceNumber:
                validate.required(newDriver.licenceNumber, 'Licence number') ??
                validate.ukLicenceNumber(newDriver.licenceNumber),
            email:
                validate.required(newDriver.email, 'Email address') ??
                validate.email(newDriver.email),
            dateOfBirth:
                validate.required(newDriver.dateOfBirth, 'Date of birth') ??
                validate.dateOfBirth(newDriver.dateOfBirth),
            phone: validate.ukPhone(newDriver.phone ?? ''),
            addressLine1: validate.required(newDriver.addressLine1, 'Address line 1'),
            city: validate.required(newDriver.city, 'City'),
            postcode:
                validate.required(newDriver.postcode, 'Postcode') ??
                validate.ukPostcode(newDriver.postcode),
        });

        setErrors(result);
        return Object.keys(result).length === 0;
    };

    const handleChange = (field: keyof CreateDriverRequest, value: string) => {
        setNewDriver(prev => ({ ...prev, [field]: value }));
        // Clear the error for that field as soon as the user edits it
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        await onSubmit(newDriver);
        setNewDriver(emptyForm);
        setErrors({});
    };

    const handleClose = () => {
        setNewDriver(emptyForm);
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    const inputClass = (field: keyof CreateDriverRequest) =>
        `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            errors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300'
        }`;

    const ringStyle = { '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Add New Driver</h3>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-4">

                        {/* First Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                            <input
                                type="text"
                                className={inputClass('firstName')}
                                style={ringStyle}
                                value={newDriver.firstName}
                                onChange={(e) => handleChange('firstName', e.target.value)}
                            />
                            {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                        </div>

                        {/* Last Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                            <input
                                type="text"
                                className={inputClass('lastName')}
                                style={ringStyle}
                                value={newDriver.lastName}
                                onChange={(e) => handleChange('lastName', e.target.value)}
                            />
                            {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                        </div>

                        {/* Licence Number */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Licence Number *</label>
                            <input
                                type="text"
                                placeholder="e.g., SMITH801125AB9CD"
                                maxLength={16}
                                className={inputClass('licenceNumber')}
                                style={ringStyle}
                                value={newDriver.licenceNumber}
                                onChange={(e) => handleChange('licenceNumber', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                            />
                            {errors.licenceNumber
                                ? <p className="mt-1 text-sm text-red-600">{errors.licenceNumber}</p>
                                : <p className="mt-1 text-xs text-gray-500">16-character UK driving licence number</p>
                            }
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                            <input
                                type="email"
                                className={inputClass('email')}
                                style={ringStyle}
                                value={newDriver.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                            />
                            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                        </div>

                        {/* Date of Birth */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                            <input
                                type="date"
                                className={inputClass('dateOfBirth')}
                                style={ringStyle}
                                value={newDriver.dateOfBirth}
                                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                            />
                            {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                            <input
                                type="tel"
                                placeholder="07700 900000"
                                className={inputClass('phone')}
                                style={ringStyle}
                                value={newDriver.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                            />
                            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                        </div>

                        {/* Address Line 1 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 *</label>
                            <input
                                type="text"
                                placeholder="Street address"
                                className={inputClass('addressLine1')}
                                style={ringStyle}
                                value={newDriver.addressLine1}
                                onChange={(e) => handleChange('addressLine1', e.target.value)}
                            />
                            {errors.addressLine1 && <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>}
                        </div>

                        {/* Address Line 2 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                            <input
                                type="text"
                                placeholder="Apartment, suite, etc. (optional)"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                                style={ringStyle}
                                value={newDriver.addressLine2}
                                onChange={(e) => handleChange('addressLine2', e.target.value)}
                            />
                        </div>

                        {/* City + Postcode */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                                <input
                                    type="text"
                                    placeholder="London"
                                    className={inputClass('city')}
                                    style={ringStyle}
                                    value={newDriver.city}
                                    onChange={(e) => handleChange('city', e.target.value)}
                                />
                                {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode *</label>
                                <input
                                    type="text"
                                    placeholder="SW1A 1AA"
                                    className={inputClass('postcode')}
                                    style={ringStyle}
                                    value={newDriver.postcode}
                                    onChange={(e) => handleChange('postcode', e.target.value.toUpperCase())}
                                />
                                {errors.postcode && <p className="mt-1 text-sm text-red-600">{errors.postcode}</p>}
                            </div>
                        </div>

                        {/* Site */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Site/Location</label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                                style={ringStyle}
                                value={newDriver.siteId || ''}
                                onChange={(e) => handleChange('siteId', e.target.value)}
                                disabled={sitesLoading}
                            >
                                <option value="">No Site Assigned</option>
                                {sites.map(site => (
                                    <option key={site.id} value={site.id}>
                                        {site.name} ({site.code})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating}
                            className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center"
                            style={{ backgroundColor: '#69BE28', '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                        >
                            {isCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {isCreating ? 'Creating...' : 'Create Driver'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddDriverModal;
